import { supabase } from '@/integrations/supabase/client';

export interface VenueGame {
  id: string;
  venue_id: string;
  posted_by: string;
  posted_by_role: string;
  sport_id?: number;
  game_title: string;
  game_description?: string;
  game_date: string;
  game_time: string;
  field_location?: string;
  status: string;
  approved_by?: string;
  approved_at?: string;
  scorekeeper_points: number;
  scorekeeper_needed: boolean;
  scorekeeper_assigned?: string;
  player_participants?: string[];
  home_team_id?: number;
  away_team_id?: number;
  home_team_status?: string;
  away_team_status?: string;
  game_status?: string;
  game_code?: string;
  home_team?: any;
  away_team?: any;
  team?: any;
  created_at: string;
  updated_at: string;
  [key: string]: any; // Allow additional fields from joins
}

export interface VenueEvent {
  id: string;
  venue_id: string;
  posted_by: string;
  posted_by_role: string;
  event_title: string;
  event_description?: string;
  event_date: string;
  event_time: string;
  event_location?: string;
  status: string;
  approved_by?: string;
  approved_at?: string;
  max_participants?: number;
  current_participants: number;
  registration_required: boolean;
  created_at: string;
  updated_at: string;
  [key: string]: any; // Allow additional fields from joins
}

export const venueGamesService = {
  // Get upcoming games for a venue
  async getUpcomingGames(venueId: string): Promise<VenueGame[]> {
    console.log('🎯 VENUE SERVICE - Getting upcoming games for venue:', venueId);
    
    const { data, error } = await supabase
      .from('venue_games')
      .select(`
        *,
        home_team:teams!venue_games_home_team_id_fkey(team_name, team_id),
        away_team:teams!venue_games_away_team_id_fkey(team_name, team_id),
        sport:available_sports(id, sport_name)
      `)
      .eq('venue_id', venueId)
      .eq('status', 'approved')
      .gte('game_date', new Date().toISOString().split('T')[0])
      .order('game_date', { ascending: true })
      .order('game_time', { ascending: true });

    if (error) {
      console.error('🎯 VENUE SERVICE - Error loading games:', error);
      throw error;
    }
    
    console.log('🎯 VENUE SERVICE - Loaded games with team names:', data);
    return data || [];
  },

  // Get upcoming events for a venue
  async getUpcomingEvents(venueId: string): Promise<VenueEvent[]> {
    console.log('🎯 VENUE SERVICE - Getting upcoming events for venue:', venueId);
    
    const { data, error } = await supabase
      .from('venue_events')
      .select('*')
      .eq('venue_id', venueId)
      .eq('status', 'approved')
      .gte('event_date', new Date().toISOString().split('T')[0])
      .order('event_date', { ascending: true })
      .order('event_time', { ascending: true });

    if (error) {
      console.error('🎯 VENUE SERVICE - Error loading events:', error);
      throw error;
    }
    
    console.log('🎯 VENUE SERVICE - Loaded events:', data);
    return data || [];
  },

  // Submit a new game
  async submitGame(gameData: any): Promise<VenueGame> {
    console.log('🎯 VENUE SERVICE - Submitting game with data:', gameData);
    
    // Validate required fields
    if (!gameData.venue_id || !gameData.posted_by) {
      throw new Error('Missing required fields: venue_id or posted_by');
    }

    // Ensure proper data types and UUID formatting
    const submitData = {
      venue_id: String(gameData.venue_id),
      posted_by: String(gameData.posted_by),
      posted_by_role: gameData.posted_by_role || 'venue',
      game_title: gameData.game_title,
      game_description: gameData.game_description || '',
      game_date: gameData.game_date,
      game_time: gameData.game_time,
      field_location: gameData.field_location || '',
      sport_id: gameData.sport_id ? parseInt(gameData.sport_id) : null,
      scorekeeper_needed: Boolean(gameData.scorekeeper_needed),
      scorekeeper_points: Number(gameData.scorekeeper_points) || 50,
      player_participants: Array.isArray(gameData.player_participants) ? gameData.player_participants : [],
      // All game submissions start as pending and require approval
      status: 'pending',
      // Handle team assignments
      home_team_status: gameData.home_team_id ? 'assigned' : 'pending',
      away_team_status: gameData.away_team_id ? 'assigned' : 'pending',
      home_team_id: gameData.home_team_id ? parseInt(gameData.home_team_id) : null,
      away_team_id: gameData.away_team_id ? parseInt(gameData.away_team_id) : null
    };

    console.log('🎯 VENUE SERVICE - Final submit data:', submitData);

    try {
      console.log('🎯 VENUE SERVICE - About to insert venue game using secure function');
      console.log('🎯 VENUE SERVICE - Submit data:', submitData);
      
      // Use the secure function to bypass RLS issues
      const { data, error } = await supabase
        .rpc('insert_venue_game', {
          p_venue_id: submitData.venue_id,
          p_posted_by: submitData.posted_by,
          p_posted_by_role: submitData.posted_by_role,
          p_game_title: submitData.game_title,
          p_game_description: submitData.game_description || '',
          p_game_date: submitData.game_date,
          p_game_time: submitData.game_time,
          p_field_location: submitData.field_location || '',
          p_sport_id: submitData.sport_id,
          p_scorekeeper_needed: submitData.scorekeeper_needed,
          p_scorekeeper_points: submitData.scorekeeper_points,
          p_player_participants: submitData.player_participants || [],
          p_home_team_id: submitData.home_team_id,
          p_away_team_id: submitData.away_team_id
        });
      
      if (error) {
        console.error('🎯 VENUE SERVICE - Function call error:', error);
        throw error;
      }
      
      // Now fetch the created game with all details
      const gameId = data;
      const { data: gameData, error: fetchError } = await supabase
        .from('venue_games')
        .select('*, game_code')
        .eq('id', gameId)
        .single();

      if (fetchError) {
        console.error('🎯 VENUE SERVICE - Error fetching created game:', fetchError);
        throw fetchError;
      }

      console.log('🎯 VENUE SERVICE - Success, returning:', gameData);
      
      // Send game code notifications to all relevant parties
      await this.sendGameCodeNotifications(gameData, gameData);
      
      return gameData;
    } catch (error) {
      console.error('🎯 VENUE SERVICE - Submission failed:', error);
      throw error;
    }
  },

  // Submit a new event
  async submitEvent(eventData: any): Promise<VenueEvent> {
    const { data, error } = await supabase
      .from('venue_events')
      .insert({
        ...eventData,
        // All event submissions start as pending and require approval
        status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get approved games for venue
  async getApprovedGames(venueId: string): Promise<VenueGame[]> {
    const { data, error } = await supabase
      .from('venue_games')
      .select(`
        *,
        home_team:teams!venue_games_home_team_id_fkey(team_id, team_name, team_logo),
        away_team:teams!venue_games_away_team_id_fkey(team_id, team_name, team_logo)
      `)
      .eq('venue_id', venueId)
      .eq('status', 'approved')
      .gte('game_date', new Date().toISOString().split('T')[0])
      .order('game_date', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Get rejected games for venue (last 30 days)
  async getRejectedGames(venueId: string): Promise<VenueGame[]> {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const { data, error } = await supabase
      .from('venue_games')
      .select(`
        *,
        home_team:teams!venue_games_home_team_id_fkey(team_id, team_name, team_logo),
        away_team:teams!venue_games_away_team_id_fkey(team_id, team_name, team_logo)
      `)
      .eq('venue_id', venueId)
      .eq('status', 'rejected')
      .gte('updated_at', thirtyDaysAgo.toISOString())
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Approve/reject game with proper status update
  async updateGameStatus(gameId: string, status: 'approved' | 'rejected', approvedBy: string, reason?: string): Promise<VenueGame> {
    try {
      const updateData: any = {
        status,
        approved_by: approvedBy,
        approved_at: new Date().toISOString()
      };

      if (status === 'rejected') {
        updateData.rejected_by = approvedBy;
        updateData.rejected_at = new Date().toISOString();
        updateData.rejection_reason = reason;
      }

      const { data, error } = await supabase
        .from('venue_games')
        .update(updateData)
        .eq('id', gameId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('🎯 VENUE SERVICE - Error updating game status:', error);
      throw error;
    }
  },

  // Approve/reject event (venue owners only)
  async updateEventStatus(eventId: string, status: 'approved' | 'rejected', approvedBy: string): Promise<VenueEvent> {
    const { data, error } = await supabase
      .from('venue_events')
      .update({
        status,
        approved_by: approvedBy,
        approved_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get pending submissions for venue owners
  async getPendingSubmissions(venueId: string): Promise<{ games: VenueGame[], events: VenueEvent[] }> {
    console.log('🎯 VENUE SERVICE - Getting pending submissions for venue:', venueId);
    
    const [gamesResponse, eventsResponse] = await Promise.all([
      supabase
        .from('venue_games')
        .select(`
          *,
          home_team:teams!venue_games_home_team_id_fkey(team_name, team_id),
          away_team:teams!venue_games_away_team_id_fkey(team_name, team_id)
        `)
        .eq('venue_id', venueId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false }),
      
      supabase
        .from('venue_events')
        .select('*')
        .eq('venue_id', venueId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
    ]);

    if (gamesResponse.error) {
      console.error('🎯 VENUE SERVICE - Error loading pending games:', gamesResponse.error);
      throw gamesResponse.error;
    }
    if (eventsResponse.error) {
      console.error('🎯 VENUE SERVICE - Error loading pending events:', eventsResponse.error);
      throw eventsResponse.error;
    }

    console.log('🎯 VENUE SERVICE - Loaded pending submissions:', {
      games: gamesResponse.data?.length || 0,
      events: eventsResponse.data?.length || 0
    });

    return {
      games: gamesResponse.data || [],
      events: eventsResponse.data || []
    };
  },

  // Assign scorekeeper to game
  async assignScorekeeper(gameId: string, scorekeeperId: string): Promise<VenueGame> {
    const { data, error } = await supabase
      .from('venue_games')
      .update({ scorekeeper_assigned: scorekeeperId })
      .eq('id', gameId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update venue game
  async updateVenueGame(gameId: string, updates: Partial<VenueGame>): Promise<VenueGame> {
    console.log('🎯 VENUE SERVICE - Updating venue game:', { gameId, updates });
    
    try {
      const { data, error } = await supabase
        .from('venue_games')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameId)
        .select(`
          *,
          home_team:teams!venue_games_home_team_id_fkey(team_name, team_id),
          away_team:teams!venue_games_away_team_id_fkey(team_name, team_id),
          sport:available_sports(id, sport_name)
        `)
        .single();

      if (error) {
        console.error('🎯 VENUE SERVICE - Update error:', error);
        throw error;
      }

      console.log('🎯 VENUE SERVICE - Game updated successfully:', data);
      return data;
    } catch (error) {
      console.error('🎯 VENUE SERVICE - Update failed:', error);
      throw error;
    }
  },

  // Send game code notifications to all relevant parties
  async sendGameCodeNotifications(gameData: VenueGame, originalSubmissionData: any): Promise<void> {
    console.log('🎯 GAME CODE NOTIFICATIONS - Starting notification process');
    console.log('🎯 Game Code:', gameData.game_code);
    console.log('🎯 Game ID:', gameData.id);
    
    try {
      const gameCode = gameData.game_code;
      const gameTitle = gameData.game_title;
      const gameDate = new Date(gameData.game_date).toLocaleDateString();
      const gameTime = gameData.game_time;
      
      // Base notification message
      const message = `Game Code ${gameCode} has been generated for "${gameTitle}" scheduled for ${gameDate} at ${gameTime}. Use this code to track and manage the game.`;
      
      // Array to collect all recipients
      const recipients: Array<{ 
        id: string, 
        role: string, 
        description: string,
        userType: 'supabase' | 'internal'
      }> = [];

      // 1. Add venue (if different from submitter)
      if (gameData.venue_id !== gameData.posted_by) {
        // Check if venue exists in profiles (Supabase user)
        const { data: venueProfile } = await supabase
          .from('profiles')
          .select('id, role')
          .eq('id', gameData.venue_id)
          .single();
        
        if (venueProfile) {
          recipients.push({
            id: gameData.venue_id,
            role: 'venue',
            description: 'Venue Owner',
            userType: 'supabase'
          });
        } else {
          // Check if venue is internal user
          const { data: internalVenue } = await supabase
            .from('internal_users')
            .select('id, role')
            .eq('id', gameData.venue_id)
            .single();
          
          if (internalVenue) {
            recipients.push({
              id: gameData.venue_id,
              role: 'venue',
              description: 'Venue Owner',
              userType: 'internal'
            });
          }
        }
      }

      // 2. Add submitter (coach/team owner)
      if (gameData.posted_by) {
        // Check if submitter exists in profiles (Supabase user)
        const { data: submitterProfile } = await supabase
          .from('profiles')
          .select('id, role')
          .eq('id', gameData.posted_by)
          .single();
        
        if (submitterProfile) {
          recipients.push({
            id: gameData.posted_by,
            role: gameData.posted_by_role,
            description: `${gameData.posted_by_role} (Submitter)`,
            userType: 'supabase'
          });
        } else {
          // Check if submitter is internal user
          const { data: internalSubmitter } = await supabase
            .from('internal_users')
            .select('id, role')
            .eq('id', gameData.posted_by)
            .single();
          
          if (internalSubmitter) {
            recipients.push({
              id: gameData.posted_by,
              role: gameData.posted_by_role,
              description: `${gameData.posted_by_role} (Submitter)`,
              userType: 'internal'
            });
          }
        }
      }

      // 3. Add team coaches for assigned teams
      const teamIds = [gameData.home_team_id, gameData.away_team_id].filter(Boolean);
      
      for (const teamId of teamIds) {
        const { data: teamCoaches } = await supabase
          .from('team_coaches')
          .select('coach_id')
          .eq('team_id', teamId);
        
        if (teamCoaches) {
          for (const coach of teamCoaches) {
            // Check if coach exists in profiles
            const { data: coachProfile } = await supabase
              .from('profiles')
              .select('id, role')
              .eq('id', coach.coach_id)
              .single();
            
            if (coachProfile) {
              recipients.push({
                id: coach.coach_id,
                role: 'coach',
                description: `Team Coach (Team ID: ${teamId})`,
                userType: 'supabase'
              });
            } else {
              // Check if coach is internal user
              const { data: internalCoach } = await supabase
                .from('internal_users')
                .select('id, role')
                .eq('id', coach.coach_id)
                .single();
              
              if (internalCoach) {
                recipients.push({
                  id: coach.coach_id,
                  role: 'coach',
                  description: `Team Coach (Team ID: ${teamId})`,
                  userType: 'internal'
                });
              }
            }
          }
        }
      }

      // 4. Add team owners for assigned teams
      for (const teamId of teamIds) {
        const { data: team } = await supabase
          .from('teams')
          .select('created_by')
          .eq('team_id', teamId)
          .single();
        
        if (team && team.created_by) {
          // Check if team owner exists in profiles
          const { data: ownerProfile } = await supabase
            .from('profiles')
            .select('id, role')
            .eq('id', team.created_by)
            .single();
          
          if (ownerProfile) {
            recipients.push({
              id: team.created_by,
              role: 'team-owner',
              description: `Team Owner (Team ID: ${teamId})`,
              userType: 'supabase'
            });
          } else {
            // Check if team owner is internal user
            const { data: internalOwner } = await supabase
              .from('internal_users')
              .select('id, role')
              .eq('id', team.created_by)
              .single();
            
            if (internalOwner) {
              recipients.push({
                id: team.created_by,
                role: 'team-owner',
                description: `Team Owner (Team ID: ${teamId})`,
                userType: 'internal'
              });
            }
          }
        }
      }

      // Remove duplicates
      const uniqueRecipients = recipients.filter((recipient, index, self) => 
        index === self.findIndex(r => r.id === recipient.id)
      );

      console.log('🎯 GAME CODE NOTIFICATIONS - Recipients:', uniqueRecipients);

      // Send notifications through the unified notification system
      for (const recipient of uniqueRecipients) {
        try {
          await supabase
            .from('game_notifications')
            .insert({
              game_id: gameData.id,
              recipient_id: recipient.id,
              recipient_type: recipient.userType,
              notification_type: 'game_code_generated',
              message: `${message} (Sent to ${recipient.description})`,
              requires_action: false,
              action_type: null
            });
          
          console.log(`✓ Game code notification sent to ${recipient.description} (${recipient.id})`);
        } catch (notifError) {
          console.error(`❌ Failed to send notification to ${recipient.id}:`, notifError);
        }
      }

      console.log('🎯 GAME CODE NOTIFICATIONS - Process completed');
      console.log(`✓ Sent notifications to ${uniqueRecipients.length} recipients`);
      
    } catch (error) {
      console.error('🎯 GAME CODE NOTIFICATIONS - Error:', error);
      // Don't throw - notification failure shouldn't break game submission
    }
  },

  // Send status change notifications to teams and coaches
  async sendStatusNotifications(gameData: VenueGame, status: 'approved' | 'rejected', updatedBy: string): Promise<void> {
    console.log('🎯 STATUS NOTIFICATIONS - Starting notification process for status:', status);
    
    try {
      const getTeamName = (team: any): string => {
        if (!team) return 'TBD';
        if (Array.isArray(team)) return team[0]?.team_name || 'TBD';
        return team.team_name || 'TBD';
      };
      const gameTitle = `${getTeamName(gameData.home_team)} vs ${getTeamName(gameData.away_team)}`;
      const gameDate = new Date(gameData.game_date).toLocaleDateString();
      const gameTime = gameData.game_time;
      
      const message = status === 'approved' 
        ? `Game "${gameTitle}" scheduled for ${gameDate} at ${gameTime} has been APPROVED by the venue. Game code: ${gameData.game_code}`
        : `Game "${gameTitle}" scheduled for ${gameDate} at ${gameTime} has been REJECTED by the venue.`;
      
      // Array to collect all recipients
      const recipients: Array<{ 
        id: string, 
        role: string, 
        description: string,
        userType: 'supabase' | 'internal'
      }> = [];

      // Add submitter (coach/team owner)
      if (gameData.posted_by && gameData.posted_by !== updatedBy) {
        // Check if submitter exists in profiles (Supabase user)
        const { data: submitterProfile } = await supabase
          .from('profiles')
          .select('id, role')
          .eq('id', gameData.posted_by)
          .single();
        
        if (submitterProfile) {
          recipients.push({
            id: gameData.posted_by,
            role: gameData.posted_by_role,
            description: `${gameData.posted_by_role} (Submitter)`,
            userType: 'supabase'
          });
        } else {
          // Check if submitter is internal user
          const { data: internalSubmitter } = await supabase
            .from('internal_users')
            .select('id, role')
            .eq('id', gameData.posted_by)
            .single();
          
          if (internalSubmitter) {
            recipients.push({
              id: gameData.posted_by,
              role: gameData.posted_by_role,
              description: `${gameData.posted_by_role} (Submitter)`,
              userType: 'internal'
            });
          }
        }
      }

      // Add team coaches for assigned teams
      const teamIds = [gameData.home_team_id, gameData.away_team_id].filter(Boolean);
      
      for (const teamId of teamIds) {
        const { data: teamCoaches } = await supabase
          .from('team_coaches')
          .select('coach_id')
          .eq('team_id', teamId);
        
        if (teamCoaches) {
          for (const coach of teamCoaches) {
            // Skip if this is the person who updated the status
            if (coach.coach_id === updatedBy) continue;
            
            // Check if coach exists in profiles
            const { data: coachProfile } = await supabase
              .from('profiles')
              .select('id, role')
              .eq('id', coach.coach_id)
              .single();
            
            if (coachProfile) {
              recipients.push({
                id: coach.coach_id,
                role: 'coach',
                description: `Team Coach (Team ID: ${teamId})`,
                userType: 'supabase'
              });
            } else {
              // Check if coach is internal user
              const { data: internalCoach } = await supabase
                .from('internal_users')
                .select('id, role')
                .eq('id', coach.coach_id)
                .single();
              
              if (internalCoach) {
                recipients.push({
                  id: coach.coach_id,
                  role: 'coach',
                  description: `Team Coach (Team ID: ${teamId})`,
                  userType: 'internal'
                });
              }
            }
          }
        }

        // Add team owners
        const { data: teamOwners } = await supabase
          .from('teams')
          .select('created_by')
          .eq('team_id', teamId)
          .single();
        
        if (teamOwners && teamOwners.created_by !== updatedBy) {
          // Check if team owner exists in profiles
          const { data: ownerProfile } = await supabase
            .from('profiles')
            .select('id, role')
            .eq('id', teamOwners.created_by)
            .single();
          
          if (ownerProfile) {
            recipients.push({
              id: teamOwners.created_by,
              role: 'team-owner',
              description: `Team Owner (Team ID: ${teamId})`,
              userType: 'supabase'
            });
          } else {
            // Check if team owner is internal user
            const { data: internalOwner } = await supabase
              .from('internal_users')
              .select('id, role')
              .eq('id', teamOwners.created_by)
              .single();
            
            if (internalOwner) {
              recipients.push({
                id: teamOwners.created_by,
                role: 'team-owner',
                description: `Team Owner (Team ID: ${teamId})`,
                userType: 'internal'
              });
            }
          }
        }
      }

      // Remove duplicates
      const uniqueRecipients = recipients.filter((recipient, index, self) => 
        index === self.findIndex(r => r.id === recipient.id)
      );

      console.log('🎯 STATUS NOTIFICATIONS - Recipients:', uniqueRecipients);

      // Send notifications through the unified notification system
      for (const recipient of uniqueRecipients) {
        try {
          await supabase
            .from('game_notifications')
            .insert({
              game_id: gameData.id,
              recipient_id: recipient.id,
              recipient_type: recipient.userType,
              notification_type: `game_${status}`,
              message: `${message} (Sent to ${recipient.description})`,
              requires_action: status === 'approved',
              action_type: status === 'approved' ? 'accept_game' : null
            });
          
          console.log(`✓ Status notification sent to ${recipient.description} (${recipient.id})`);
        } catch (notifError) {
          console.error(`❌ Failed to send notification to ${recipient.id}:`, notifError);
        }
      }

      console.log('🎯 STATUS NOTIFICATIONS - Process completed');
      console.log(`✓ Sent ${status} notifications to ${uniqueRecipients.length} recipients`);
      
    } catch (error) {
      console.error('🎯 STATUS NOTIFICATIONS - Error:', error);
      // Don't throw - notification failure shouldn't break status update
    }
  },

  // Get available games that teams can join
  async getAvailableGames(filters: {
    sport_id?: number;
    date_from?: string;
    date_to?: string;
    location?: string;
    status?: string;
  } = {}): Promise<VenueGame[]> {
    try {
      let query = supabase
        .from('venue_games')
        .select(`
          *,
          home_team:teams!venue_games_home_team_id_fkey(team_id, team_name, team_logo),
          away_team:teams!venue_games_away_team_id_fkey(team_id, team_name, team_logo),
          sport:available_sports(id, sport_name)
        `)
        .eq('status', 'approved')
        .gte('game_date', new Date().toISOString().split('T')[0])
        .order('game_date', { ascending: true });

      // Apply filters
      if (filters.sport_id) {
        query = query.eq('sport_id', filters.sport_id);
      }
      
      if (filters.date_from) {
        query = query.gte('game_date', filters.date_from);
      }
      
      if (filters.date_to) {
        query = query.lte('game_date', filters.date_to);
      }
      
      if (filters.location) {
        query = query.ilike('field_location', `%${filters.location}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching available games:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAvailableGames:', error);
      throw error;
    }
  }
};
